import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Button, Col, Input, Row, Select,
  Form, Radio, RadioChangeEvent, notification,
} from 'antd';
import './ServiceEstimate.scss';
import {
  get, isSuperAgentError, RequestWrapper, ResponseWrapper,
} from '../../../../dataSource/requests';
import { getUserPermissions, getUserType } from '../../../../utils/users';

const ServiceEstimate = (props): JSX.Element => {
  interface SericeEstimateForm {
    nameCTC: string;
    choiceServiceType: number;
    flagCTC: number;
    branchCoopercitrus: number;
  }

  const { nextPage, params, previousPage } = props;
  const navigate = useNavigate();
  const userPermissions = getUserPermissions();
  const userType = getUserType();
  const [formInstance] = Form.useForm();
  const [typesRequest] = useState([
    {
      id: 1,
      name: 'Solicitação de Orçamento',
    },
    {
      id: 2,
      name: 'Solicitação de Serviço',
    },
  ]);
  const [valueCTC, setValueCTC] = useState();
  const [blockInputNameCTC, setBlockInputNameCTC] = useState<boolean>(true);
  const [configInputNameCTC, setConfigInputNameCTC] = useState(
    <Form.Item
      label=""
      name="nameCTC"
      rules={[
        {
          required: false, message: '',
        },
      ]}
    >
      <Input
        disabled={blockInputNameCTC}
        placeholder="Insira o nome do CTC"
      />
    </Form.Item>,
  );
  const [branchsCoopercitrus, setBranchsCoopercitrus] = useState([
    {
      id: 0,
      name: 'Nenhuma filial encontrada',
    },
  ]);

  const getListAllBranchs = async (): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = get({
      url: '/branch/listAllBranchCoopercitrus',

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 13000):
        // "Branch coopercitrus listed successfully", 13000
        setBranchsCoopercitrus(response?.body?.listAllBranchCoopercitrusOutputs);
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao buscar as filiais, por favor '
            + 'tente novamente mais tarde.',
        });
        break;
    }
  };

  useEffect(() => {
    getListAllBranchs();

    return () => {
      setBlockInputNameCTC(true);
    };
  }, []);

  const configRenderInputNameCTC = (flag: boolean): void => {
    if (flag) {
      setConfigInputNameCTC(
        <Form.Item
          label=""
          name="nameCTC"
          rules={[
            {
              required: false, message: '',
            },
          ]}
        >
          <Input
            disabled
            placeholder="Insira o nome do CTC"
          />
        </Form.Item>,
      );
    } else {
      setConfigInputNameCTC(
        <Form.Item
          label=""
          name="nameCTC"
          rules={[
            {
              required: true, message: 'Por favor, insira o nome do CTC',
            },
          ]}
        >
          <Input
            disabled={false}
            placeholder="Insira o nome do CTC"
          />
        </Form.Item>,
      );
    }
  };

  const handleChangeCTC = (e: RadioChangeEvent): void => {
    setValueCTC(e.target.value);

    if (e.target.value === 1) {
      setBlockInputNameCTC(false);
      configRenderInputNameCTC(false);
    } else {
      formInstance.setFieldsValue({
        nameCTC: '',
      });
      setBlockInputNameCTC(true);
      configRenderInputNameCTC(true);
      formInstance.validateFields(['nameCTC']);
    }
  };

  const handleSubmit = async (values: SericeEstimateForm): Promise<void> => {
    await formInstance.validateFields();
    const targetBranchName = branchsCoopercitrus.find(
      (i) => { return i.id === values.branchCoopercitrus; },
    );

    if (params.selectedService.idCode === 'POS_VENDAS' && userPermissions.includes('Solicitar novo serviço')) {
      params.choiceServiceType = 2; // solitação de serviço
    } else {
      switch (true) {
        case (
          userType === 'COOPERATIVE'
          && userPermissions.includes('Solicitar novo orçamento')
          && userPermissions.includes('Solicitar novo serviço')
        ):
          params.choiceServiceType = values.choiceServiceType;
          break;

        case (
          userType === 'COOPERATIVE'
          && userPermissions.includes('Solicitar novo serviço')
        ):
          params.choiceServiceType = 2; // solicitação de serviço
          break;

        case (
          userType === 'COOPERATIVE'
          && userPermissions.includes('Solicitar novo orçamento')
        ):
          params.choiceServiceType = 1; // solicitação de orçamento
          break;

        case (
          userType === 'COOPERCITRUS'
        ):
          params.choiceServiceType = 2; // solicitação de serviço
          break;

        default:
          break;
      }
    }

    params.haveCtc = (values.flagCTC === 1 ? true : false);
    params.ctc = values.nameCTC;
    params.branchCoopercitrusId = values.branchCoopercitrus;
    params.branchName = targetBranchName.name;

    nextPage(params);
  };

  return (
    <div className="ServiceEstimate">
      <Form
        scrollToFirstError
        layout="vertical"
        form={formInstance}
        onFinish={handleSubmit}
        onFinishFailed={
          (errorInfo): void => {
            console.log('onFinishFailed', errorInfo);
          }
        }
      >
        {
          params.selectedService.idCode !== 'POS_VENDAS'
            && userType === 'COOPERATIVE'
            && userPermissions.includes('Solicitar novo orçamento')
            && userPermissions.includes('Solicitar novo serviço')
            ? (
              <Row gutter={24}>
                <Col xs={{ span: 24 }} lg={{ span: 10 }}>
                  <Form.Item
                    label={<span className="select-property-text">O que deseja solicitar?</span>}
                    name="choiceServiceType"
                    rules={[
                      { required: true, message: 'Por favor, selecione o tipo' },
                    ]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="children"
                    >
                      {
                        typesRequest.map((v) => (
                          <Select.Option key={v.id} value={v.id}>
                            {v.name}
                          </Select.Option>
                        ))
                      }
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            )
            : null
        }

        <Row gutter={24}>
          <Col xs={{ span: 24 }} lg={{ span: 10 }}>
            <Form.Item
              label={<span className="select-property-text">Possui CTC que te atende?</span>}
              name="flagCTC"
              rules={[
                { required: true, message: 'Por favor, selecione o uma opção' },
              ]}
            >
              <Radio.Group onChange={handleChangeCTC} value={valueCTC} style={{ marginLeft: '1rem' }}>
                <Radio value={1}>Sim</Radio>
                <Radio value={2}>Não</Radio>
              </Radio.Group>
            </Form.Item>

            {configInputNameCTC}
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={{ span: 24 }} lg={{ span: 10 }}>
            <Form.Item
              label={(
                <span className="select-property-text">
                  {
                    userType === 'COOPERCITRUS'
                      ? 'Selecione a filial Coopercitrus'
                      : 'Por qual filial Coopercitrus é atendido(a)?'
                  }
                </span>
              )}
              name="branchCoopercitrus"
              rules={[
                { required: true, message: 'Por favor, selecione a filial' },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="children"
              >
                {
                  branchsCoopercitrus.map((v) => (
                    <Select.Option key={v.id} value={v.id}>
                      {v.name}
                    </Select.Option>
                  ))
                }
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <div className="controls-container">
          <Form.Item
            noStyle
          >
            <Button
              size="large"
              className="btn-previus-step"
              onClick={
                (): void => {
                  previousPage({
                    selectedService: {
                      id: 0,
                      name: '',
                      description: '',
                      types: [],
                    },
                  });
                }
              }
            >
              Cancelar
            </Button>
          </Form.Item>
          <Form.Item
            noStyle
          >
            <Button
              size="large"
              type="primary"
              htmlType="submit"
            >
              Prosseguir
            </Button>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

ServiceEstimate.propTypes = {
  previousPage: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,

  params: PropTypes.shape({
    selectedService: PropTypes.objectOf(PropTypes.any).isRequired,
    service: PropTypes.string,
    selectedMatriculation: PropTypes.string,
    createdLandId: PropTypes.number,
    choiceServiceType: PropTypes.number,
    haveCtc: PropTypes.bool,
    ctc: PropTypes.string,
    branchCoopercitrusId: PropTypes.number,
    branchName: PropTypes.string,
  }).isRequired,
};

ServiceEstimate.defaultProps = {};

export default ServiceEstimate;
